import { PocTooltip } from 'commons/components/components-wrapper';
import { Placement, PopoverOffset, PopoverPositioningStrategy } from '@one/web-components';
import { useAsyncTooltipMount } from 'commons/hooks';
import { Trans } from 'core/i18n';
import { useTranslation } from 'core/i18n/i18n';
import React from 'react';
import { StyledTooltipedText } from './tooltiped-text.style';

export interface TooltipedTextComponentProps {
    i18nKey: string;
    tooltipText: string;
    className?: string;
    i18nValues?: Record<string, unknown>;
    i18nComponents?: Record<string, React.ReactElement>;
    tooltipAnchorClassname?: string;
    showTooltipAlways?: boolean;
    strategy?: PopoverPositioningStrategy;
    placement?: Placement;
    offset?: PopoverOffset;
    mountDelayMs?: number;
    hideDelayMs?: number;
}

const defaultVerticalOffset = 8;
const defaultHorizontalOffset = 0;

export const TooltipedTextComponent: React.FC<TooltipedTextComponentProps> = (props) => {
    const {
        i18nKey,
        tooltipText,
        i18nValues,
        i18nComponents,
        tooltipAnchorClassname,
        showTooltipAlways = false,
        className = '',
        strategy = 'fixed',
        placement = 'top',
        offset = [defaultHorizontalOffset, defaultVerticalOffset],
        mountDelayMs,
        hideDelayMs,
    } = props;

    const { t } = useTranslation();

    const shouldShowTooltip = (ev: React.MouseEvent<HTMLSpanElement>) => {
        const parent = ev.currentTarget.parentNode as HTMLElement | null;
        if (!parent) {
            return showTooltipAlways;
        }
        return showTooltipAlways || parent.scrollWidth > parent.clientWidth;
    };

    const { tooltipAnchorId, isMountedTooltip, isVisibleTooltip, onMouseEnter, onMouseLeave } = useAsyncTooltipMount({
        mountDelayMs,
        hideDelayMs,
        shouldShowTooltip,
    });

    return (
        <StyledTooltipedText className={className}>
            <Trans
                t={t}
                i18nKey={i18nKey}
                values={i18nValues}
                components={{
                    ...i18nComponents,
                    tooltip: (
                        <span
                            id={tooltipAnchorId}
                            role="tooltip"
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            className={tooltipAnchorClassname}
                        />
                    ),
                }}
            />

            {isMountedTooltip ? (
                <PocTooltip
                    anchor={tooltipAnchorId}
                    strategy={strategy}
                    placement={placement}
                    offset={offset}
                    disableTriggers
                    visible={isVisibleTooltip}
                >
                    {tooltipText}
                </PocTooltip>
            ) : null}
        </StyledTooltipedText>
    );
};
